import { recordClick } from './actions'

const ELEMENTS_NEEDING_MANUAL_TRACKING = 'button, [role=button]'
const INTERACTIVE_ELEMENT_SELECTOR = `a, ${ELEMENTS_NEEDING_MANUAL_TRACKING}`
const TRACKABLE_ELEMENT_ATTR_NAME = 'data-bactmln'
const ARIA_EXPANDED_ATTR_NAME = 'aria-expanded'
const TOGGLE_VALUE_ATTR_NAME = 'data-toggle-value'
const TRACKING_NAME_ATTRIBUTES = ['id']
let UNTRACKED_SELECTOR_PARENTS = [
  '.video-js',
  '.playkit-gui-area',
  '#onetrust-consent-sdk',
  '#chat-placeholder'
]
const KEY_DESTINATION_URL = 'DestinationURL'
const KEY_TOGGLE_VALUE = 'ToggleValue'
const KEY_TOGGLE_OPEN_CLOSE = 'ToggleOpenClose'
const KEY_TOGGLE_EXPAND_COLLAPSE = 'ToggleExpandCollapse'
const VTIM_REPLACEMENT_KEYS = [
  KEY_DESTINATION_URL,
  KEY_TOGGLE_VALUE,
  KEY_TOGGLE_OPEN_CLOSE,
  KEY_TOGGLE_EXPAND_COLLAPSE
]

/** @type {string | null} */
let viewport = null
let useVTIM = false
let appendDevice = true
let appendAriaExpanded = false
let appendAriaExpandedInversion = false

/**
 * @param {boolean} enableVTIMTracking
 * @param {boolean} enableDeviceTracking
 * @param {boolean} enableAppendAriaExpanded
 * @param {boolean} enableAppendAriaExpandedInversion
 * @param {string} customTrackingAttribute
 * @param {string | string[]} untrackedSelectors
 * @returns {TeardownFunction}
 */
export default (
  enableVTIMTracking,
  enableDeviceTracking,
  enableAppendAriaExpanded,
  enableAppendAriaExpandedInversion,
  customTrackingAttribute,
  untrackedSelectors
) => {
  const USE_CAPTURING = true
  document.addEventListener('click', handleDocumentClick, USE_CAPTURING)

  if (untrackedSelectors) {
    if (typeof untrackedSelectors === 'string') {
      UNTRACKED_SELECTOR_PARENTS.unshift(untrackedSelectors)
    }
    if (Array.isArray(untrackedSelectors)) {
      UNTRACKED_SELECTOR_PARENTS = UNTRACKED_SELECTOR_PARENTS.concat(untrackedSelectors)
    }
  }

  if (customTrackingAttribute) {
    TRACKING_NAME_ATTRIBUTES.unshift(customTrackingAttribute)
  }
  useVTIM = enableVTIMTracking
  if (useVTIM) {
    TRACKING_NAME_ATTRIBUTES.unshift('data-tracking-name')
  }
  appendDevice = enableDeviceTracking
  if (!appendDevice) tagInteractiveElements()
  appendAriaExpanded = enableAppendAriaExpanded
  appendAriaExpandedInversion = enableAppendAriaExpandedInversion

  const observer = new MutationObserver(observerCallback)
  observer.observe(document.body, { attributes: true, childList: true, subtree: true })

  return () => {
    observer.disconnect()
    document.removeEventListener('click', handleDocumentClick, USE_CAPTURING)
  }
}

/**
 * @param {string} newViewport
 */
export const setViewport = newViewport => {
  viewport = newViewport
  tagInteractiveElements()
}

/**
 * @param {Node} node
 * @returns {node is Element}
 */
const isElement = node => {
  return node.nodeType === node.ELEMENT_NODE
}

/**
 * @param {Node} node
 */
const isInteractive = node => {
  return isElement(node) && node.matches(INTERACTIVE_ELEMENT_SELECTOR)
}

/**
 * @param {Node} node
 */
const containsInteractive = node => {
  return isElement(node) && node.querySelector(INTERACTIVE_ELEMENT_SELECTOR) !== null
}

/** @type {MutationCallback} */
const observerCallback = mutations => {
  let needsTagging = false

  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      if (isInteractive(mutation.target) && TRACKING_NAME_ATTRIBUTES.includes(mutation.attributeName)) {
        needsTagging = true
        break
      }
    }

    if (mutation.type === 'childList' && mutation.addedNodes.length) {
      if ([...mutation.addedNodes].some(node => isInteractive(node) || containsInteractive(node))) {
        needsTagging = true
        break
      }
    }
  }

  if (needsTagging) {
    tagInteractiveElements()
    if (window.bactm) window.bactm.rescanDOM()
  }
}

export const tagInteractiveElements = () => {
  const interactiveElements = Array.prototype.slice.call(document.querySelectorAll(INTERACTIVE_ELEMENT_SELECTOR))
  interactiveElements.map(element => {

    for (const selector of UNTRACKED_SELECTOR_PARENTS) {
      if (element.closest(selector)) return
    }

    let attrValue = null
    for (const attr of TRACKING_NAME_ATTRIBUTES) {
      attrValue = element.getAttribute(attr)
      if (attrValue) {
        if (useVTIM) {
          attrValue = doVTIMKeyReplacement(attr, element)
        } else {
          if (appendDevice) {
            attrValue += '_' + viewport
          }
        }
        break
      }
    }

    if (attrValue) {
      element.setAttribute(TRACKABLE_ELEMENT_ATTR_NAME, attrValue)
    } else {
      console.log(`Tealium Error: Element could not be set up for tracking. Missing one of attributes: ${TRACKING_NAME_ATTRIBUTES.join(', ')}`, element)
    }
  })
}

export const trackableElementAttrName = () => TRACKABLE_ELEMENT_ATTR_NAME

/**
 * @param {string} attr
 * @param {Element} element
 * @returns {string}
 */
const doVTIMKeyReplacement = (attr, element) => {
  const segments = element.getAttribute(attr).split('-_-')
  for (const ix in segments) {
    if (VTIM_REPLACEMENT_KEYS.includes(segments[ix])) {
      segments[ix] = getVTIMSegmentReplacement(segments[ix], element)
    }
    if (segments[ix]) {
      const subsegments = segments[ix].split('_')
      for (const ix in subsegments) {
        if (VTIM_REPLACEMENT_KEYS.includes(subsegments[ix])) {
          subsegments[ix] = getVTIMSegmentReplacement(subsegments[ix], element)
        }
      }
      segments[ix] = subsegments.join('_')
    }
  }

  return segments.join('-_-')
}

/**
 *
 * @param {string} segment
 * @param {Element} element
 */
const getVTIMSegmentReplacement = (segment, element) => {
  const isExpanded = (el) => {
    let value = el.getAttribute(ARIA_EXPANDED_ATTR_NAME) === 'true'
    if (appendAriaExpandedInversion) {
      value = !value
    }
    return value
  }

  switch (segment) {
    case KEY_DESTINATION_URL:
      return element.getAttribute('href')

    case KEY_TOGGLE_VALUE:
      return element.getAttribute(TOGGLE_VALUE_ATTR_NAME)

    case KEY_TOGGLE_EXPAND_COLLAPSE:
      return isExpanded(element) ? 'Collapse' : 'Expand'
    case KEY_TOGGLE_OPEN_CLOSE:
      return isExpanded(element) ? 'Close' : 'Open'
  }
  console.error(`unhandled case ${segment}`, element)
  return segment
}

/**
 * @param {Element} element
 */
const getTrackingId = element => {
  let trackingId = element.getAttribute(TRACKABLE_ELEMENT_ATTR_NAME)
  if (appendAriaExpanded && element.getAttribute(ARIA_EXPANDED_ATTR_NAME)) {
    const parts = trackingId.split('_')
    const insertPosition = appendDevice ? parts.length - 1 : parts.length
    const matchValue = appendAriaExpandedInversion ? 'false' : 'true'
    const elementAction = element.getAttribute(ARIA_EXPANDED_ATTR_NAME) === matchValue ? 'expand' : 'collapse'
    parts.splice(insertPosition, 0, elementAction)
    trackingId = parts.join('_')
  }
  return trackingId
}

/**
 * @param {{ target: Element }} event
 */
const handleDocumentClick = ({ target }) => {
  if (useVTIM) return // In VTIM mode, non-anchors don't need manual link clicks
  if (target.closest('.video-js')) return // Make sure not to hit elements added by videojs
  const element = findTrackableElement(target)

  if (element && element.hasAttribute(TRACKABLE_ELEMENT_ATTR_NAME) && element.matches(ELEMENTS_NEEDING_MANUAL_TRACKING)) {
    recordClick('#', getTrackingId(element))
  }
}

/**
 *
 * @param {Element} target
 * @returns {Element | false}
 */
const findTrackableElement = target => {
  if (checkIfHasTrackableAttr(target)) return target
  const closest = target.closest(INTERACTIVE_ELEMENT_SELECTOR)
  if (closest) return closest

  return false
}

/**
 * @param {Element} target
 * @returns {string | null}
 */
const checkIfHasTrackableAttr = target => {
  return target.getAttribute(TRACKABLE_ELEMENT_ATTR_NAME)
}
